/**
 * Configuration for subscription email detection
 * This config drives both Gmail search queries and post-pull filtering
 * All patterns and weights are configurable without code changes
 */

export const SUBSCRIPTION_EMAIL_CONFIG = {
  // Gmail search query configuration
  search: {
    // Time range
    daysBack: 1095, // 3 years

    // Sender patterns (transactional email indicators)
    fromPatterns: [
      'no-reply',
      'noreply',
      'billing',
      'payments',
      'support',
      'subscriptions',
      'accounts',
      'receipts',
      'invoices',
      'auto-reply'
    ],

    // Subject keyword groups
    subjectKeywords: {
      invoice: ['invoice', 'receipt', 'payment confirmation', 'payment received', 'Your receipt from', 'Your invoice'],
      renewal: ['renewed', 'renewal', 'renew', 'renews on', 'will renew', 'Your plan has renewed'],
      subscription: ['subscription', 'membership', 'your plan', 'your new plan', 'Subscription Confirmation', 'Your subscription is confirmed', 'Your new plan'],
      cancellation: ['canceled', 'cancellation', 'cancelled', 'sorry to see you go', 'subscription ended'],
      paymentFailed: ['payment failed', 'payment declined', 'update payment', 'payment issue'],
      trial: ['trial started', 'trial ending', 'trial ends', 'free trial'],
      planChange: ['plan updated', 'switched to', 'upgraded to', 'downgraded to']
    },

    // Attachment filters
    attachments: {
      enabled: true,
      fileTypes: ['pdf'],
      combinedWithKeywords: ['invoice', 'receipt', 'statement']
    },

    // Exclusions
    excludeLabels: ['spam', 'trash', 'sent', 'drafts'],
    excludeKeywords: ['newsletter', 'unsubscribe to stop']
  },

  // Post-pull filtering configuration
  filtering: {
    // Envelope-level signals
    envelopeSignals: {
      autoSubmittedHeader: { weight: 0.2, requiredValue: 'auto-generated' },
      transactionalDomains: { weight: 0.15, patterns: ['noreply', 'no-reply', 'transactional', 'notifications'] },
      returnPathMatch: { weight: 0.1 } // Return-Path matches From
    },

    // Keyword scoring weights
    keywordWeights: {
      subject: {
        invoice: 0.25,
        renewal: 0.25,
        subscription: 0.20,
        cancellation: 0.25,
        paymentFailed: 0.25,
        trial: 0.20,
        planChange: 0.20
      },
      body: {
        invoice: 0.15,
        renewal: 0.15,
        subscription: 0.10,
        cancellation: 0.15,
        paymentFailed: 0.15,
        trial: 0.10,
        planChange: 0.10
      }
    },

    // MIME/attachment signals
    attachmentSignals: {
      pdfInvoice: { weight: 0.25, requiredKeywords: ['invoice', 'receipt'] },
      anyPdf: { weight: 0.10 }
    },

    // Anti-patterns (things that indicate NOT a subscription email)
    antiPatterns: {
      marketing: {
        weight: -0.3,
        keywords: ['shop now', 'limited time', 'sale ends', 'click here', 'buy now', '% off']
      },
      shipping: {
        weight: -0.4,
        keywords: ['shipped', 'tracking number', 'out for delivery', 'delivered']
      },
      socialNotification: {
        weight: -0.3,
        keywords: ['liked your', 'commented on', 'followed you', 'sent you a message']
      }
    },

    // Confidence threshold (0-1)
    confidenceThreshold: 0.6,

    // Deduplication
    deduplication: {
      useMessageId: true,
      useThreadId: true,
      similarityThreshold: 0.9 // For fuzzy matching subjects
    }
  },

  // Event categorization rules
  categorization: {
    rules: [
      {
        type: 'cancellation',
        priority: 1,
        subjectKeywords: ['canceled', 'cancelled', 'cancellation', 'sorry to see you go', 'subscription ended'],
        bodyKeywords: ['no longer', 'terminated', 'cancelled your', 'ended your subscription'],
        minConfidence: 0.7
      },
      {
        type: 'payment_failed',
        priority: 1,
        subjectKeywords: ['payment failed', 'payment declined', 'card declined', 'update payment'],
        bodyKeywords: ['payment failed', 'unable to charge', 'payment method', 'update your card'],
        minConfidence: 0.7
      },
      {
        type: 'trial_ending',
        priority: 2,
        subjectKeywords: ['trial ending', 'trial ends', 'trial expiring'],
        bodyKeywords: ['free trial', 'trial period', 'will be charged'],
        minConfidence: 0.6
      },
      {
        type: 'trial_started',
        priority: 2,
        subjectKeywords: ['trial started', 'welcome', 'free trial'],
        bodyKeywords: ['trial period', 'try free', 'trial begins'],
        minConfidence: 0.6
      },
      {
        type: 'renewal',
        priority: 3,
        subjectKeywords: ['renewed', 'renewal', 'auto-renewed', 'subscription renewed'],
        bodyKeywords: ['renewed', 'next renewal', 'renews on', 'automatically renewed'],
        minConfidence: 0.6
      },
      {
        type: 'plan_change',
        priority: 3,
        subjectKeywords: ['plan updated', 'upgraded', 'downgraded', 'switched to', 'changed your plan'],
        bodyKeywords: ['new plan', 'plan change', 'switched from', 'upgraded to'],
        minConfidence: 0.6
      },
      {
        type: 'plan_start',
        priority: 3,
        subjectKeywords: ['subscription created', 'successfully subscribed', 'welcome to'],
        bodyKeywords: ['subscription starts', 'first billing', 'welcome to your'],
        minConfidence: 0.6
      },
      {
        type: 'invoice',
        priority: 4,
        subjectKeywords: ['invoice', 'receipt', 'payment confirmation'],
        bodyKeywords: ['invoice', 'receipt', 'amount charged', 'payment received'],
        minConfidence: 0.6
      }
    ],
    fallbackType: 'unknown'
  }
};
